import { useEffect, useRef } from 'react';

// Generic paged carousel: renders each child as a full-size panel the user
// pages between by swipe/scroll/trackpad, with the active panel controlled
// externally (activeIndex/onIndexChange) so callers can also jump panels
// programmatically (e.g. clicking a book to jump into a highlight mode).
// Knows nothing about what the panels contain - new modes are just new
// children plus another index constant at the call site.
export default function Carousel({ activeIndex, onIndexChange, children }) {
  const containerRef = useRef(null);
  const suppressScrollSync = useRef(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const target = Math.round(activeIndex * el.clientWidth);
    if (Math.abs(el.scrollLeft - target) > 2) {
      suppressScrollSync.current = true;
      el.scrollTo({ left: target, behavior: 'smooth' });
    }
  }, [activeIndex]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    let settleTimer;
    function handleScroll() {
      clearTimeout(settleTimer);
      settleTimer = setTimeout(() => {
        if (suppressScrollSync.current) {
          suppressScrollSync.current = false;
          return;
        }
        const index = Math.round(el.scrollLeft / el.clientWidth);
        onIndexChange(index);
      }, 100);
    }

    function handleWheel(event) {
      if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
        el.scrollLeft += event.deltaY;
        event.preventDefault();
      }
    }

    el.addEventListener('scroll', handleScroll, { passive: true });
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      clearTimeout(settleTimer);
      el.removeEventListener('scroll', handleScroll);
      el.removeEventListener('wheel', handleWheel);
    };
  }, [onIndexChange]);

  return (
    <div className="carousel" ref={containerRef}>
      {children.map((child, i) => (
        <div className="carousel-panel" key={i}>
          {child}
        </div>
      ))}
    </div>
  );
}
